"use client";

import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { FiInfo, FiVolume2 } from "react-icons/fi";
import { LuImage } from "react-icons/lu";
import { MdDeleteOutline } from "react-icons/md";
import ConfirmPopUp from "../../popup/confirmPopUp";
import StatusPopUp from "../../popup/statusPopUp";
import uploadAPI from "../../../services/upload/uploadAPI";
import mainAPI from "../../../services/main/mainAPI";
import correctAnswerAPI from "../../../services/correctAnswer/correctAnswerAPI";

type CorrectAnswerItem = {
    id: number;
    no: number;
    correctAnswer: string;
    wrongAnswer: string;
    imageCorrectAnswerFileName: string;
    imageCorrectAnswerFile: File | null;
    imageCorrectAnswerUrl: string | null;
    soundCorrectAnswerFileName: string;
    soundCorrectAnswerFile: File | null;
    soundCorrectAnswerUrl: string | null;
    imageWrongAnswerFileName: string;
    imageWrongAnswerFile: File | null;
    imageWrongAnswerUrl: string | null;
    soundWrongAnswerFileName: string;
    soundWrongAnswerFile: File | null;
    soundWrongAnswerUrl: string | null;
    hasHint: boolean;
    hintText: string;
    hintImageFileName: string;
    hintImageFile: File | null;
    hintImageUrl: string | null;
    hintAudioFileName: string;
    hintAudioFile: File | null;
    hintAudioUrl: string | null;
};

type UploadTarget = {
    key: string;
    file: File;
    fileName: string;
    sectionName: string;
    refId: string;
};

type GameSkills = {
    skill_listening_speaking: boolean;
    skill_lang_nature: boolean;
    skill_reading: boolean;
    skill_writing: boolean;
};

type GameSkillKey = keyof GameSkills;

const DEFAULT_GAME_SKILLS: GameSkills = {
    skill_listening_speaking: false,
    skill_lang_nature: false,
    skill_reading: false,
    skill_writing: false,
};

type WhackAMoleProps = {
    createdGameData?: {
        id: number | string;
        exercise_name?: string;
        uuid?: string;
        description?: string | null;
    } | null;
    initialGameDetail?: {
        game_info?: {
            id?: number | string;
            uuid?: string;
            exercise_name?: string;
            suggestion?: string | null;
            description?: string | null;
            skill_listening_speaking?: boolean | "true" | "false" | null;
            skill_lang_nature?: boolean | "true" | "false" | null;
            skill_reading?: boolean | "true" | "false" | null;
            skill_writing?: boolean | "true" | "false" | null;
        };
        questions?: Array<{
            id?: number;
            no?: number;
            keyword?: string;
            question?: string;
            matching_word?: string;
            answer?: string;
            hint?: string | null;
            image_keyword?: string | null;
            sound_keyword?: string | null;
            image_matching_word?: string | null;
            sound_matching_word?: string | null;
            correct_answer?: string | null;
            wrong_answer?: string | null;
            image_correct_answer?: string | null;
            sound_correct_answer?: string | null;
            image_wrong_answer?: string | null;
            sound_wrong_answer?: string | null;
            image_hint?: string | null;
            sound_hint?: string | null;
            skill_listening_speaking?: boolean | "true" | "false" | null;
            skill_lang_nature?: boolean | "true" | "false" | null;
            skill_reading?: boolean | "true" | "false" | null;
            skill_writing?: boolean | "true" | "false" | null;
        }>;
    } | null;
    onCompleted?: () => void;
};

const MIN_ITEMS = 2;
const MAX_ITEMS = 5;

function getFileExtension(fileName: string) {
    const dotIndex = fileName.lastIndexOf(".");
    if (dotIndex < 0 || dotIndex === fileName.length - 1) {
        return "";
    }
    return fileName.slice(dotIndex);
}

function getFileNameFromPath(path: string | null | undefined) {
    if (!path) {
        return "";
    }

    const cleanPath = String(path);
    const bySlash = cleanPath.split("/");
    const candidate = bySlash[bySlash.length - 1] || "";
    return decodeURIComponent(candidate);
}

function buildUploadedMediaUrl(path: string | null | undefined) {
    if (!path) {
        return null;
    }

    const raw = String(path).trim();
    if (!raw) {
        return null;
    }

    if (raw.startsWith("http://") || raw.startsWith("https://")) {
        return raw;
    }

    const baseUrl = process.env.NEXT_PUBLIC_BACKEND_URL || "https://thaipilacreate.gforcesolution.com";
    const normalizedBaseUrl = baseUrl.replace(/\/$/, "");
    return `${normalizedBaseUrl}/api/${encodeURIComponent(raw)}`;
}

function normalizeSkillBoolean(value: unknown): boolean | null {
    if (typeof value === "boolean") {
        return value;
    }

    if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (normalized === "true") {
            return true;
        }
        if (normalized === "false") {
            return false;
        }
    }

    return null;
}

function normalizeGameSkillsOrNull(value: Partial<Record<GameSkillKey, unknown>>) {
    const listening = normalizeSkillBoolean(value.skill_listening_speaking);
    const langNature = normalizeSkillBoolean(value.skill_lang_nature);
    const reading = normalizeSkillBoolean(value.skill_reading);
    const writing = normalizeSkillBoolean(value.skill_writing);

    if (listening === null || langNature === null || reading === null || writing === null) {
        return null;
    }

    return {
        skill_listening_speaking: listening,
        skill_lang_nature: langNature,
        skill_reading: reading,
        skill_writing: writing,
    };
}

function extractGameSkillsFromUnknown(value: Partial<Record<GameSkillKey, unknown>>): GameSkills {
    const normalized = normalizeGameSkillsOrNull(value);
    return normalized ?? { ...DEFAULT_GAME_SKILLS };
}

function createEmptyCorrectAnswerItem(id: number, no: number): CorrectAnswerItem {
    return {
        id,
        no,
        correctAnswer: "",
        wrongAnswer: "",
        imageCorrectAnswerFileName: "",
        imageCorrectAnswerFile: null,
        imageCorrectAnswerUrl: null,
        soundCorrectAnswerFileName: "",
        soundCorrectAnswerFile: null,
        soundCorrectAnswerUrl: null,
        imageWrongAnswerFileName: "",
        imageWrongAnswerFile: null,
        imageWrongAnswerUrl: null,
        soundWrongAnswerFileName: "",
        soundWrongAnswerFile: null,
        soundWrongAnswerUrl: null,
        hasHint: false,
        hintText: "",
        hintImageFileName: "",
        hintImageFile: null,
        hintImageUrl: null,
        hintAudioFileName: "",
        hintAudioFile: null,
        hintAudioUrl: null,
    };
}

export default function WhackAMole({ createdGameData, initialGameDetail, onCompleted }: WhackAMoleProps) {
    const nextCorrectAnswerIdRef = useRef(6);
    const initialExerciseNameRef = useRef(createdGameData?.exercise_name ?? "");
    const initialSuggestionRef = useRef("");
    const initialDescriptionRef = useRef("");
    const initialCorrectAnswerIdsRef = useRef<Set<number>>(new Set());
    const initialGameSkillsRef = useRef<GameSkills>({ ...DEFAULT_GAME_SKILLS });

    const [exerciseName, setExerciseName] = useState(createdGameData?.exercise_name ?? "");
    const [suggestion, setSuggestion] = useState("");
    const [description, setDescription] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isConfirmSaveOpen, setIsConfirmSaveOpen] = useState(false);
    const [isUpdateMode, setIsUpdateMode] = useState(Boolean(initialGameDetail?.game_info?.id));
    const [statusPopUp, setStatusPopUp] = useState<{
        title: string;
        message: string;
        type: "info" | "success" | "error";
    } | null>(null);
    const [previewIframeUrl, setPreviewIframeUrl] = useState<string | null>(null);
    const [gameSkills, setGameSkills] = useState<GameSkills>({ ...DEFAULT_GAME_SKILLS });
    const [items, setItems] = useState<CorrectAnswerItem[]>([
        createEmptyCorrectAnswerItem(1, 1),
        createEmptyCorrectAnswerItem(2, 2),
    ]);

    const canAddItem = items.length < MAX_ITEMS;
    const canRemoveItem = items.length > MIN_ITEMS;

    const previewUuid = String(
        initialGameDetail?.game_info?.uuid ?? createdGameData?.uuid ?? ""
    ).trim();
    const canPreview = isUpdateMode && Boolean(previewUuid);

    useEffect(() => {
        setExerciseName(createdGameData?.exercise_name ?? "");
        initialExerciseNameRef.current = createdGameData?.exercise_name ?? "";
    }, [createdGameData?.exercise_name]);

    useEffect(() => {
        if (initialGameDetail?.game_info?.id) {
            return;
        }

        const incomingDescription = String(createdGameData?.description ?? "");
        setDescription(incomingDescription);
        initialDescriptionRef.current = incomingDescription;
    }, [createdGameData?.description, initialGameDetail?.game_info?.id]);

    const applyLoadedGameDetail = (detail: WhackAMoleProps["initialGameDetail"]) => {
        if (!detail?.game_info?.id) {
            const createdDescription = String(createdGameData?.description ?? "");
            setSuggestion("");
            setDescription(createdDescription);
            initialSuggestionRef.current = "";
            initialDescriptionRef.current = createdDescription;
            initialCorrectAnswerIdsRef.current = new Set();
            initialGameSkillsRef.current = { ...DEFAULT_GAME_SKILLS };
            setGameSkills({ ...DEFAULT_GAME_SKILLS });
            setIsUpdateMode(false);
            return;
        }

        const gameInfo = detail.game_info;
        const incomingRows = Array.isArray(detail.questions) ? detail.questions : [];

        const incomingExerciseName = String(gameInfo.exercise_name ?? "");
        const incomingSuggestion = String(gameInfo.suggestion ?? "");
        const incomingDescription = String(gameInfo.description ?? "");

        setExerciseName(incomingExerciseName);
        setSuggestion(incomingSuggestion);
        setDescription(incomingDescription);
        initialExerciseNameRef.current = incomingExerciseName;
        initialSuggestionRef.current = incomingSuggestion;
        initialDescriptionRef.current = incomingDescription;

        const mappedItems = incomingRows.length > 0
            ? incomingRows.map((row, index) => {
                const record = row as Record<string, unknown>;
                const mappedId = Number(record.id ?? index + 1);
                const normalizedId = Number.isInteger(mappedId) && mappedId > 0 ? mappedId : index + 1;
                const correctAnswerRaw = String(record.correct_answer ?? record.keyword ?? record.question ?? "");
                const wrongAnswerRaw = String(record.wrong_answer ?? record.matching_word ?? record.answer ?? "");
                const hintRaw = String(record.hint ?? "");
                const imageHintRaw = String(record.image_hint ?? "");
                const soundHintRaw = String(record.sound_hint ?? "");
                return {
                    id: normalizedId,
                    no: Number(record.no ?? index + 1),
                    correctAnswer: correctAnswerRaw,
                    wrongAnswer: wrongAnswerRaw,
                    imageCorrectAnswerFileName: getFileNameFromPath(String(record.image_correct_answer ?? record.image_keyword ?? "")),
                    imageCorrectAnswerFile: null,
                    imageCorrectAnswerUrl: buildUploadedMediaUrl(String(record.image_correct_answer ?? record.image_keyword ?? "")),
                    soundCorrectAnswerFileName: getFileNameFromPath(String(record.sound_correct_answer ?? record.sound_keyword ?? "")),
                    soundCorrectAnswerFile: null,
                    soundCorrectAnswerUrl: buildUploadedMediaUrl(String(record.sound_correct_answer ?? record.sound_keyword ?? "")),
                    imageWrongAnswerFileName: getFileNameFromPath(String(record.image_wrong_answer ?? record.image_matching_word ?? "")),
                    imageWrongAnswerFile: null,
                    imageWrongAnswerUrl: buildUploadedMediaUrl(String(record.image_wrong_answer ?? record.image_matching_word ?? "")),
                    soundWrongAnswerFileName: getFileNameFromPath(String(record.sound_wrong_answer ?? record.sound_matching_word ?? "")),
                    soundWrongAnswerFile: null,
                    soundWrongAnswerUrl: buildUploadedMediaUrl(String(record.sound_wrong_answer ?? record.sound_matching_word ?? "")),
                    hasHint: Boolean(hintRaw.trim() || imageHintRaw || soundHintRaw),
                    hintText: hintRaw,
                    hintImageFileName: getFileNameFromPath(imageHintRaw),
                    hintImageFile: null,
                    hintImageUrl: buildUploadedMediaUrl(imageHintRaw),
                    hintAudioFileName: getFileNameFromPath(soundHintRaw),
                    hintAudioFile: null,
                    hintAudioUrl: buildUploadedMediaUrl(soundHintRaw),
                };
            })
            : [
                createEmptyCorrectAnswerItem(1, 1),
                createEmptyCorrectAnswerItem(2, 2),
            ];

        const skillsFromGameInfo = extractGameSkillsFromUnknown({
            skill_listening_speaking: gameInfo.skill_listening_speaking,
            skill_lang_nature: gameInfo.skill_lang_nature,
            skill_reading: gameInfo.skill_reading,
            skill_writing: gameInfo.skill_writing,
        });

        const firstRow = incomingRows[0] as Record<string, unknown> | undefined;
        const skillsFromRows = extractGameSkillsFromUnknown({
            skill_listening_speaking: firstRow?.skill_listening_speaking,
            skill_lang_nature: firstRow?.skill_lang_nature,
            skill_reading: firstRow?.skill_reading,
            skill_writing: firstRow?.skill_writing,
        });

        const resolvedSkills = incomingRows.length > 0 ? skillsFromRows : skillsFromGameInfo;
        setGameSkills(resolvedSkills);
        initialGameSkillsRef.current = resolvedSkills;

        const normalizedItems = mappedItems.map((item, index) => ({ ...item, no: index + 1 }));
        setItems(normalizedItems);

        initialCorrectAnswerIdsRef.current = new Set(normalizedItems.map((item) => item.id));
        nextCorrectAnswerIdRef.current = normalizedItems.reduce((max, item) => Math.max(max, item.id), 0) + 1;
        setIsUpdateMode(true);
    };

    useEffect(() => {
        applyLoadedGameDetail(initialGameDetail);
    }, [initialGameDetail]);

    const setNoByOrder = (sourceItems: CorrectAnswerItem[]) =>
        sourceItems.map((item, index) => ({ ...item, no: index + 1 }));

    const updateGameSkill = (skillKey: GameSkillKey, checked: boolean) => {
        setGameSkills((previous) => ({
            ...previous,
            [skillKey]: checked,
        }));
    };

    const addItem = () => {
        if (!canAddItem) {
            return;
        }
        const newId = nextCorrectAnswerIdRef.current;
        nextCorrectAnswerIdRef.current += 1;
        setItems((previous) => setNoByOrder([...previous, createEmptyCorrectAnswerItem(newId, previous.length + 1)]));
    };

    const removeItem = (id: number) => {
        if (!canRemoveItem) {
            return;
        }
        setItems((previous) => setNoByOrder(previous.filter((item) => item.id !== id)));
    };

    const moveItem = (id: number, direction: -1 | 1) => {
        setItems((previous) => {
            const currentIndex = previous.findIndex((item) => item.id === id);
            if (currentIndex < 0) {
                return previous;
            }

            const nextIndex = currentIndex + direction;
            if (nextIndex < 0 || nextIndex >= previous.length) {
                return previous;
            }

            const reordered = [...previous];
            const [moved] = reordered.splice(currentIndex, 1);
            reordered.splice(nextIndex, 0, moved);
            return setNoByOrder(reordered);
        });
    };

    const updateItem = (id: number, updater: (item: CorrectAnswerItem) => CorrectAnswerItem) => {
        setItems((previous) =>
            previous.map((item) => (item.id === id ? updater(item) : item))
        );
    };

    const updateTextField = (id: number, field: "correctAnswer" | "wrongAnswer", value: string) => {
        updateItem(id, (item) => ({ ...item, [field]: value }));
    };

    const setFileOnItem = (
        id: number,
        options: {
            file: File;
            fileNameField:
            | "imageCorrectAnswerFileName"
            | "soundCorrectAnswerFileName"
            | "imageWrongAnswerFileName"
            | "soundWrongAnswerFileName"
            | "hintImageFileName"
            | "hintAudioFileName";
            fileField:
            | "imageCorrectAnswerFile"
            | "soundCorrectAnswerFile"
            | "imageWrongAnswerFile"
            | "soundWrongAnswerFile"
            | "hintImageFile"
            | "hintAudioFile";
            urlField:
            | "imageCorrectAnswerUrl"
            | "soundCorrectAnswerUrl"
            | "imageWrongAnswerUrl"
            | "soundWrongAnswerUrl"
            | "hintImageUrl"
            | "hintAudioUrl";
            forceEnableHint?: boolean;
        }
    ) => {
        updateItem(id, (item) => ({
            ...item,
            [options.fileNameField]: options.file.name,
            [options.fileField]: options.file,
            [options.urlField]: null,
            ...(options.forceEnableHint ? { hasHint: true } : {}),
        }));
    };

    const enableHint = (id: number) => {
        updateItem(id, (item) => ({ ...item, hasHint: true }));
    };

    const clearHint = (id: number) => {
        updateItem(id, (item) => ({
            ...item,
            hasHint: false,
            hintText: "",
            hintImageFileName: "",
            hintImageFile: null,
            hintImageUrl: null,
            hintAudioFileName: "",
            hintAudioFile: null,
            hintAudioUrl: null,
        }));
    };

    const collectUploadTargets = (sourceItems: CorrectAnswerItem[]) => {
        const targets: UploadTarget[] = [];

        sourceItems.forEach((item, index) => {
            const timestamp = Date.now();

            if (item.imageCorrectAnswerFile && !item.imageCorrectAnswerUrl) {
                const ext = getFileExtension(item.imageCorrectAnswerFile.name);
                targets.push({
                    key: `ca-${index}-image_correct_answer`,
                    file: item.imageCorrectAnswerFile,
                    fileName: `image_correct_answer_ca${item.id}_${timestamp}${ext}`,
                    sectionName: "image_correct_answer",
                    refId: `correct_answer_${item.id}`,
                });
            }

            if (item.soundCorrectAnswerFile && !item.soundCorrectAnswerUrl) {
                const ext = getFileExtension(item.soundCorrectAnswerFile.name);
                targets.push({
                    key: `ca-${index}-sound_correct_answer`,
                    file: item.soundCorrectAnswerFile,
                    fileName: `sound_correct_answer_ca${item.id}_${timestamp}${ext}`,
                    sectionName: "sound_correct_answer",
                    refId: `correct_answer_${item.id}`,
                });
            }

            if (item.imageWrongAnswerFile && !item.imageWrongAnswerUrl) {
                const ext = getFileExtension(item.imageWrongAnswerFile.name);
                targets.push({
                    key: `ca-${index}-image_wrong_answer`,
                    file: item.imageWrongAnswerFile,
                    fileName: `image_wrong_answer_ca${item.id}_${timestamp}${ext}`,
                    sectionName: "image_wrong_answer",
                    refId: `correct_answer_${item.id}`,
                });
            }

            if (item.soundWrongAnswerFile && !item.soundWrongAnswerUrl) {
                const ext = getFileExtension(item.soundWrongAnswerFile.name);
                targets.push({
                    key: `ca-${index}-sound_wrong_answer`,
                    file: item.soundWrongAnswerFile,
                    fileName: `sound_wrong_answer_ca${item.id}_${timestamp}${ext}`,
                    sectionName: "sound_wrong_answer",
                    refId: `correct_answer_${item.id}`,
                });
            }

            if (item.hintImageFile && !item.hintImageUrl) {
                const ext = getFileExtension(item.hintImageFile.name);
                targets.push({
                    key: `ca-${index}-image_hint`,
                    file: item.hintImageFile,
                    fileName: `image_hint_ca${item.id}_${timestamp}${ext}`,
                    sectionName: "image_hint",
                    refId: `correct_answer_${item.id}`,
                });
            }

            if (item.hintAudioFile && !item.hintAudioUrl) {
                const ext = getFileExtension(item.hintAudioFile.name);
                targets.push({
                    key: `ca-${index}-sound_hint`,
                    file: item.hintAudioFile,
                    fileName: `sound_hint_ca${item.id}_${timestamp}${ext}`,
                    sectionName: "sound_hint",
                    refId: `correct_answer_${item.id}`,
                });
            }
        });

        return targets;
    };

    const uploadPendingMedia = async (targets: UploadTarget[]) => {
        const uploadedUrlByKey = new Map<string, string>();

        if (targets.length === 0) {
            return uploadedUrlByKey;
        }

        const uploadResults = await Promise.allSettled(
            targets.map((target) =>
                uploadAPI
                    .uploadMedia({
                        file: target.file,
                        fileName: target.fileName,
                        sectionName: target.sectionName,
                        ref_id: target.refId,
                    })
                    .then((response) => ({ target, response }))
            )
        );

        for (const result of uploadResults) {
            if (result.status === "rejected") {
                throw new Error("upload_failed");
            }

            const response = result.value.response as { status?: string; url?: string };
            if (response?.status === "failed" || !response?.url) {
                throw new Error("upload_failed");
            }

            uploadedUrlByKey.set(result.value.target.key, response.url);
        }

        return uploadedUrlByKey;
    };

    const applyUploadedUrlsToState = (sourceItems: CorrectAnswerItem[], uploadedUrlByKey: Map<string, string>) => {
        return sourceItems.map((item, index) => ({
            ...item,
            imageCorrectAnswerUrl: uploadedUrlByKey.get(`ca-${index}-image_correct_answer`) ?? item.imageCorrectAnswerUrl,
            imageCorrectAnswerFile: uploadedUrlByKey.get(`ca-${index}-image_correct_answer`) ? null : item.imageCorrectAnswerFile,
            soundCorrectAnswerUrl: uploadedUrlByKey.get(`ca-${index}-sound_correct_answer`) ?? item.soundCorrectAnswerUrl,
            soundCorrectAnswerFile: uploadedUrlByKey.get(`ca-${index}-sound_correct_answer`) ? null : item.soundCorrectAnswerFile,
            imageWrongAnswerUrl: uploadedUrlByKey.get(`ca-${index}-image_wrong_answer`) ?? item.imageWrongAnswerUrl,
            imageWrongAnswerFile: uploadedUrlByKey.get(`ca-${index}-image_wrong_answer`) ? null : item.imageWrongAnswerFile,
            soundWrongAnswerUrl: uploadedUrlByKey.get(`ca-${index}-sound_wrong_answer`) ?? item.soundWrongAnswerUrl,
            soundWrongAnswerFile: uploadedUrlByKey.get(`ca-${index}-sound_wrong_answer`) ? null : item.soundWrongAnswerFile,
            hintImageUrl: uploadedUrlByKey.get(`ca-${index}-image_hint`) ?? item.hintImageUrl,
            hintImageFile: uploadedUrlByKey.get(`ca-${index}-image_hint`) ? null : item.hintImageFile,
            hintAudioUrl: uploadedUrlByKey.get(`ca-${index}-sound_hint`) ?? item.hintAudioUrl,
            hintAudioFile: uploadedUrlByKey.get(`ca-${index}-sound_hint`) ? null : item.hintAudioFile,
        }));
    };

    const refreshFromServer = async () => {
        if (!previewUuid) {
            return;
        }

        const latestGameDetail = await mainAPI.getGameinfoAndQuestionByUuid(previewUuid);
        if (latestGameDetail && (latestGameDetail as { status?: string }).status !== "failed") {
            applyLoadedGameDetail(latestGameDetail as WhackAMoleProps["initialGameDetail"]);
        }
    };

    const handleSubmitWhackAMole = async () => {
        if (!createdGameData?.id) {
            setStatusPopUp({
                title: "ไม่สำเร็จ",
                message: "ไม่พบข้อมูลเกมที่สร้างล่าสุด",
                type: "error",
            });
            return;
        }

        try {
            setIsSubmitting(true);

            const trimmedExerciseName = exerciseName.trim();
            const trimmedSuggestion = suggestion.trim();
            const trimmedDescription = description.trim();
            const shouldUpdateExerciseName =
                trimmedExerciseName !== initialExerciseNameRef.current.trim();
            const shouldUpdateSuggestion =
                trimmedSuggestion !== initialSuggestionRef.current.trim();
            const shouldUpdateDescription =
                trimmedDescription !== initialDescriptionRef.current.trim();

            if (shouldUpdateExerciseName || shouldUpdateSuggestion || shouldUpdateDescription) {
                const updateResponse = await mainAPI.updateExerciseNameAndSuggestion({
                    gameId: createdGameData.id,
                    ...(shouldUpdateExerciseName ? { exercise_name: trimmedExerciseName } : {}),
                    ...(shouldUpdateSuggestion ? { suggestion: trimmedSuggestion } : {}),
                    ...(shouldUpdateDescription ? { description: trimmedDescription } : {}),
                });

                if (updateResponse?.status === "failed") {
                    setStatusPopUp({
                        title: "ไม่สำเร็จ",
                        message: "อัปเดตชื่อกิจกรรม คำแนะนำ หรือรายละเอียดไม่สำเร็จ",
                        type: "error",
                    });
                    return;
                }

                initialExerciseNameRef.current = trimmedExerciseName;
                initialSuggestionRef.current = trimmedSuggestion;
                initialDescriptionRef.current = trimmedDescription;
                setExerciseName(trimmedExerciseName);
                setSuggestion(trimmedSuggestion);
                setDescription(trimmedDescription);
            }

            const uploadTargets = collectUploadTargets(items);
            const uploadedUrlByKey = await uploadPendingMedia(uploadTargets);
            const itemsWithResolvedUrls = applyUploadedUrlsToState(items, uploadedUrlByKey);

            if (!Array.isArray(itemsWithResolvedUrls) || itemsWithResolvedUrls.length === 0) {
                setStatusPopUp({
                    title: "ไม่สำเร็จ",
                    message: "ต้องมีรายการจับคู่อย่างน้อย 1 รายการ",
                    type: "error",
                });
                return;
            }

            const normalizedGameSkills = normalizeGameSkillsOrNull(gameSkills);
            if (!normalizedGameSkills) {
                setStatusPopUp({
                    title: "ไม่สำเร็จ",
                    message: "ข้อมูลสมรรถนะไม่ถูกต้อง (ต้องเป็น true/false)",
                    type: "error",
                });
                return;
            }

            const seenNos = new Set<number>();
            for (let index = 0; index < itemsWithResolvedUrls.length; index += 1) {
                const item = itemsWithResolvedUrls[index];
                const no = index + 1;

                if (!Number.isInteger(no)) {
                    setStatusPopUp({
                        title: "ไม่สำเร็จ",
                        message: "ลำดับรายการไม่ถูกต้อง",
                        type: "error",
                    });
                    return;
                }

                if (seenNos.has(no)) {
                    setStatusPopUp({
                        title: "ไม่สำเร็จ",
                        message: "ลำดับรายการซ้ำในคำขอ",
                        type: "error",
                    });
                    return;
                }
                seenNos.add(no);

                if (!item.correctAnswer.trim()) {
                    setStatusPopUp({
                        title: "ไม่สำเร็จ",
                        message: `คำตอบที่ถูกต้องลำดับ ${no} ต้องไม่ว่าง`,
                        type: "error",
                    });
                    return;
                }

                if (!item.wrongAnswer.trim()) {
                    setStatusPopUp({
                        title: "ไม่สำเร็จ",
                        message: `คำตอบที่ไม่ถูกต้องลำดับ ${no} ต้องไม่ว่าง`,
                        type: "error",
                    });
                    return;
                }
            }

            const payloadRows = itemsWithResolvedUrls.map((item, index) => ({
                no: index + 1,
                correct_answer: item.correctAnswer.trim(),
                wrong_answer: item.wrongAnswer.trim(),
                hint: item.hasHint ? item.hintText.trim() || null : null,
                image_correct_answer: item.imageCorrectAnswerUrl || null,
                sound_correct_answer: item.soundCorrectAnswerUrl || null,
                image_wrong_answer: item.imageWrongAnswerUrl || null,
                sound_wrong_answer: item.soundWrongAnswerUrl || null,
                image_hint: item.hasHint ? item.hintImageUrl || null : null,
                sound_hint: item.hasHint ? item.hintAudioUrl || null : null,
            }));

            const response = await correctAnswerAPI.createCorrectAnswer({
                gameId: createdGameData.id,
                skill_listening_speaking: normalizedGameSkills.skill_listening_speaking,
                skill_lang_nature: normalizedGameSkills.skill_lang_nature,
                skill_reading: normalizedGameSkills.skill_reading,
                skill_writing: normalizedGameSkills.skill_writing,
                correct_answers: payloadRows,
            });

            if (response?.status === "failed") {
                setStatusPopUp({
                    title: "ไม่สำเร็จ",
                    message: "บันทึก Whack A Mole ไม่สำเร็จ",
                    type: "error",
                });
                return;
            }

            setItems(setNoByOrder(itemsWithResolvedUrls));
            initialGameSkillsRef.current = normalizedGameSkills;
            await refreshFromServer();
            setIsUpdateMode(true);
            setStatusPopUp({
                title: "สำเร็จ",
                message: "บันทึก Whack A Mole เรียบร้อยแล้ว",
                type: "success",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUpdateWhackAMole = async () => {
        if (!createdGameData?.id) {
            setStatusPopUp({
                title: "ไม่สำเร็จ",
                message: "ไม่พบข้อมูลเกมที่ต้องการอัปเดต",
                type: "error",
            });
            return;
        }

        try {
            setIsSubmitting(true);

            const trimmedExerciseName = exerciseName.trim();
            const trimmedSuggestion = suggestion.trim();
            const trimmedDescription = description.trim();
            const shouldUpdateExerciseName =
                trimmedExerciseName !== initialExerciseNameRef.current.trim();
            const shouldUpdateSuggestion =
                trimmedSuggestion !== initialSuggestionRef.current.trim();
            const shouldUpdateDescription =
                trimmedDescription !== initialDescriptionRef.current.trim();

            if (shouldUpdateExerciseName || shouldUpdateSuggestion || shouldUpdateDescription) {
                const updateResponse = await mainAPI.updateExerciseNameAndSuggestion({
                    gameId: createdGameData.id,
                    ...(shouldUpdateExerciseName ? { exercise_name: trimmedExerciseName } : {}),
                    ...(shouldUpdateSuggestion ? { suggestion: trimmedSuggestion } : {}),
                    ...(shouldUpdateDescription ? { description: trimmedDescription } : {}),
                });

                if (updateResponse?.status === "failed") {
                    setStatusPopUp({
                        title: "ไม่สำเร็จ",
                        message: "อัปเดตชื่อกิจกรรม คำแนะนำ หรือรายละเอียดไม่สำเร็จ",
                        type: "error",
                    });
                    return;
                }

                initialExerciseNameRef.current = trimmedExerciseName;
                initialSuggestionRef.current = trimmedSuggestion;
                initialDescriptionRef.current = trimmedDescription;
                setExerciseName(trimmedExerciseName);
                setSuggestion(trimmedSuggestion);
                setDescription(trimmedDescription);
            }

            const uploadTargets = collectUploadTargets(items);
            const uploadedUrlByKey = await uploadPendingMedia(uploadTargets);
            const itemsWithResolvedUrls = applyUploadedUrlsToState(items, uploadedUrlByKey);

            const normalizedGameSkills = normalizeGameSkillsOrNull(gameSkills);
            if (!normalizedGameSkills) {
                setStatusPopUp({
                    title: "ไม่สำเร็จ",
                    message: "ข้อมูลสมรรถนะไม่ถูกต้อง (ต้องเป็น true/false)",
                    type: "error",
                });
                return;
            }

            const changedSkillPayload: Partial<GameSkills> = {};
            if (normalizedGameSkills.skill_listening_speaking !== initialGameSkillsRef.current.skill_listening_speaking) {
                changedSkillPayload.skill_listening_speaking = normalizedGameSkills.skill_listening_speaking;
            }
            if (normalizedGameSkills.skill_lang_nature !== initialGameSkillsRef.current.skill_lang_nature) {
                changedSkillPayload.skill_lang_nature = normalizedGameSkills.skill_lang_nature;
            }
            if (normalizedGameSkills.skill_reading !== initialGameSkillsRef.current.skill_reading) {
                changedSkillPayload.skill_reading = normalizedGameSkills.skill_reading;
            }
            if (normalizedGameSkills.skill_writing !== initialGameSkillsRef.current.skill_writing) {
                changedSkillPayload.skill_writing = normalizedGameSkills.skill_writing;
            }

            const currentIds = new Set<number>(itemsWithResolvedUrls.map((item) => item.id));
            const deletedIds = Array.from(initialCorrectAnswerIdsRef.current).filter((id) => !currentIds.has(id));

            for (const deletedId of deletedIds) {
                const deleteResponse = await correctAnswerAPI.hardDeleteCorrectAnswer({ correctAnswerId: deletedId });
                if (deleteResponse?.status === "failed") {
                    setStatusPopUp({
                        title: "ไม่สำเร็จ",
                        message: `ลบรายการ id ${deletedId} ไม่สำเร็จ`,
                        type: "error",
                    });
                    return;
                }
            }

            const existingRows = itemsWithResolvedUrls.filter((item) => initialCorrectAnswerIdsRef.current.has(item.id));
            const newRows = itemsWithResolvedUrls.filter((item) => !initialCorrectAnswerIdsRef.current.has(item.id));

            for (const row of existingRows) {
                const updateResponse = await correctAnswerAPI.updateCorrectAnswer({
                    correctAnswerId: row.id,
                    no: row.no,
                    correct_answer: row.correctAnswer.trim(),
                    wrong_answer: row.wrongAnswer.trim(),
                    hint: row.hasHint ? row.hintText.trim() || null : null,
                    image_correct_answer: row.imageCorrectAnswerUrl || null,
                    sound_correct_answer: row.soundCorrectAnswerUrl || null,
                    image_wrong_answer: row.imageWrongAnswerUrl || null,
                    sound_wrong_answer: row.soundWrongAnswerUrl || null,
                    image_hint: row.hasHint ? row.hintImageUrl || null : null,
                    sound_hint: row.hasHint ? row.hintAudioUrl || null : null,
                    ...changedSkillPayload,
                });

                if (updateResponse?.status === "failed") {
                    setStatusPopUp({
                        title: "ไม่สำเร็จ",
                        message: `อัปเดตรายการลำดับ ${row.no} ไม่สำเร็จ`,
                        type: "error",
                    });
                    return;
                }
            }

            if (newRows.length > 0) {
                const createNewRowsResponse = await correctAnswerAPI.createCorrectAnswer({
                    gameId: createdGameData.id,
                    skill_listening_speaking: normalizedGameSkills.skill_listening_speaking,
                    skill_lang_nature: normalizedGameSkills.skill_lang_nature,
                    skill_reading: normalizedGameSkills.skill_reading,
                    skill_writing: normalizedGameSkills.skill_writing,
                    correct_answers: newRows.map((row) => ({
                        no: row.no,
                        correct_answer: row.correctAnswer.trim(),
                        wrong_answer: row.wrongAnswer.trim(),
                        hint: row.hasHint ? row.hintText.trim() || null : null,
                        image_correct_answer: row.imageCorrectAnswerUrl || null,
                        sound_correct_answer: row.soundCorrectAnswerUrl || null,
                        image_wrong_answer: row.imageWrongAnswerUrl || null,
                        sound_wrong_answer: row.soundWrongAnswerUrl || null,
                        image_hint: row.hasHint ? row.hintImageUrl || null : null,
                        sound_hint: row.hasHint ? row.hintAudioUrl || null : null,
                    })),
                });

                if (createNewRowsResponse?.status === "failed") {
                    setStatusPopUp({
                        title: "ไม่สำเร็จ",
                        message: "เพิ่มรายการใหม่ไม่สำเร็จ",
                        type: "error",
                    });
                    return;
                }
            }

            setItems(setNoByOrder(itemsWithResolvedUrls));
            await refreshFromServer();

            initialCorrectAnswerIdsRef.current = new Set(itemsWithResolvedUrls.map((item) => item.id));
            initialGameSkillsRef.current = normalizedGameSkills;
            setIsUpdateMode(true);
            setStatusPopUp({
                title: "สำเร็จ",
                message: "อัปเดต Whack A Mole เรียบร้อยแล้ว",
                type: "success",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCloseStatusPopUp = () => {
        setStatusPopUp(null);
        void onCompleted;
    };

    const handleClickFinishButton = () => {
        if (isSubmitting) {
            return;
        }
        setIsConfirmSaveOpen(true);
    };

    const handleCancelConfirmSave = () => {
        setIsConfirmSaveOpen(false);
    };

    const handleConfirmSave = () => {
        setIsConfirmSaveOpen(false);
        if (isUpdateMode) {
            void handleUpdateWhackAMole();
            return;
        }
        void handleSubmitWhackAMole();
    };

    const handleOpenPreview = () => {
        if (!canPreview) {
            return;
        }

        if (!previewUuid) {
            setStatusPopUp({
                title: "ไม่สามารถ Preview ได้",
                message: "ไม่พบ UUID ของเกมสำหรับการ Preview",
                type: "error",
            });
            return;
        }

        const previewBaseUrl = "https://thaipilagame.gforcesolution.com";
        const normalizedBaseUrl = previewBaseUrl.replace(/\/$/, "");
        const previewUrl = `${normalizedBaseUrl}/${encodeURIComponent(previewUuid)}`;
        setPreviewIframeUrl(previewUrl);
    };

    const handleClosePreview = () => {
        setPreviewIframeUrl(null);
    };

    const onFileInputChange = (
        event: ChangeEvent<HTMLInputElement>,
        callback: (file: File) => void
    ) => {
        const file = event.target.files?.[0];
        if (file) {
            callback(file);
        }
        event.currentTarget.value = "";
    };

    return (
        <section className="mx-auto w-full rounded px-4 py-3 text-[#3f3f3f] lg:px-10">
            <div className="border-b border-[#dddddd] pb-2 text-2xl leading-none">
                <span className="font-semibold text-[#4E4E4E]">แก้ไขเนื้อหา &gt; </span>
                <span className="font-bold text-[#2b2b2b]">Whack a mole</span>
            </div>

            <div className="mt-4 space-y-3">
                <div>
                    <label className="mb-1 block text-lg font-medium text-[#5b5b5b]">ชื่อกิจกรรม</label>
                    <input
                        value={exerciseName}
                        onChange={(event) => setExerciseName(event.target.value)}
                        className="h-10 w-full rounded border border-[#bfc2c8] bg-white px-3 text-lg outline-none"
                        placeholder="ไม่มีชื่อกิจกรรม"
                    />
                </div>

                <div>
                    <label className="mb-1 flex items-center gap-1 text-lg font-medium text-[#5b5b5b]">
                        คำแนะนำในการเล่น
                        <FiInfo size={14} />
                    </label>
                    <input
                        value={suggestion}
                        onChange={(event) => setSuggestion(event.target.value)}
                        className="h-10 w-full rounded border border-[#bfc2c8] bg-white px-3 text-lg outline-none"
                        placeholder="ลากและจับคู่คำให้ถูกต้อง"
                    />
                </div>

                <div>
                    <label className="mb-1 flex items-center gap-1 text-lg font-medium text-[#5b5b5b]">
                        Description / รายละเอียด
                    </label>
                    <input
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                        className="h-10 w-full rounded border border-[#bfc2c8] bg-white px-3 text-lg outline-none"
                        placeholder="ลากและจับคู่คำให้ถูกต้อง"
                    />
                </div>

            </div>

            <div className="mt-5 p-3 bg-[#f9f9f9] rounded-md border border-[#e0e0e0]">
                <div className="grid grid-cols-[40px_1fr_1fr_60px] items-center gap-4 pb-2">
                    <span></span>
                    <div className="flex flex-col items-start gap-1">
                        <span className="inline-block rounded-md bg-[#67E52C] px-10 py-1.5 text-lg font-semibold text-white">ถูกต้อง</span>
                        <span className="text-sm mt-1 font-semibold">คำตอบเหล่านี้ควรกดเลือก</span>
                    </div>
                    <div className="flex flex-col items-start gap-1">
                        <span className="inline-block rounded-md bg-[#F54043] px-10 py-1.5 text-lg font-semibold text-white">ไม่ถูกต้อง</span>
                        <span className="text-sm mt-1 font-semibold">หลีกเลี่ยงการกดปุ่มคำตอบเหล่านี้</span>
                    </div>
                    <span></span>
                </div>

                <div className="space-y-3">
                    {items.map((item, index) => {
                        const keywordImageId = `keyword-image-${item.id}`;
                        const keywordAudioId = `keyword-audio-${item.id}`;
                        const matchingImageId = `matching-image-${item.id}`;
                        const matchingAudioId = `matching-audio-${item.id}`;
                        const hintImageId = `hint-image-${item.id}`;
                        const hintAudioId = `hint-audio-${item.id}`;

                        return (
                            <div key={item.id} className="flex flex-col">
                                <div className="grid grid-cols-[40px_1fr_1fr_60px] items-center gap-4">
                                    <span className="text-lg font-semibold text-[#5a5a5a] text-center">{index + 1}.</span>

                                    <div className="relative">
                                        <div className="flex h-9.5 items-center rounded-md border border-[#a8a8a8] bg-white px-3 focus-within:border-blue-400">
                                            <input
                                                value={item.correctAnswer}
                                                onChange={(event) => updateTextField(item.id, "correctAnswer", event.target.value)}
                                                className="h-full w-full min-w-0 bg-transparent text-lg outline-none"
                                            />
                                            <div className="flex items-center gap-3 pl-2">
                                                <label htmlFor={keywordImageId} className="cursor-pointer text-[#4ca2e2] hover:text-blue-500">
                                                    <LuImage size={18} />
                                                </label>
                                                <input
                                                    id={keywordImageId}
                                                    type="file"
                                                    accept="image/*"
                                                    className="hidden"
                                                    onChange={(event) => onFileInputChange(event, (file) =>
                                                        setFileOnItem(item.id, {
                                                            file,
                                                            fileNameField: "imageCorrectAnswerFileName",
                                                            fileField: "imageCorrectAnswerFile",
                                                            urlField: "imageCorrectAnswerUrl",
                                                        })
                                                    )}
                                                />

                                                <label htmlFor={keywordAudioId} className="cursor-pointer text-[#626262] hover:text-black">
                                                    <FiVolume2 size={18} />
                                                </label>
                                                <input
                                                    id={keywordAudioId}
                                                    type="file"
                                                    accept="audio/*"
                                                    className="hidden"
                                                    onChange={(event) => onFileInputChange(event, (file) =>
                                                        setFileOnItem(item.id, {
                                                            file,
                                                            fileNameField: "soundCorrectAnswerFileName",
                                                            fileField: "soundCorrectAnswerFile",
                                                            urlField: "soundCorrectAnswerUrl",
                                                        })
                                                    )}
                                                />
                                            </div>
                                        </div>
                                        {item.imageCorrectAnswerFileName || item.soundCorrectAnswerFileName ? (
                                            <div className="mt-1 flex flex-wrap gap-x-2 text-sm text-[#6b6b6b] absolute -bottom-4 left-0">
                                                {item.imageCorrectAnswerFileName ? <span>รูป: {item.imageCorrectAnswerFileName}</span> : null}
                                                {item.soundCorrectAnswerFileName ? <span>เสียง: {item.soundCorrectAnswerFileName}</span> : null}
                                            </div>
                                        ) : null}
                                    </div>

                                    <div className="relative">
                                        <div className="flex h-9.5 items-center rounded-md border border-[#a8a8a8] bg-white px-3 focus-within:border-blue-400">
                                            <input
                                                value={item.wrongAnswer}
                                                onChange={(event) => updateTextField(item.id, "wrongAnswer", event.target.value)}
                                                className="h-full w-full min-w-0 bg-transparent text-lg outline-none"
                                            />
                                            <div className="flex items-center gap-3 pl-2">
                                                <label htmlFor={matchingImageId} className="cursor-pointer text-[#4ca2e2] hover:text-blue-500">
                                                    <LuImage size={18} />
                                                </label>
                                                <input
                                                    id={matchingImageId}
                                                    type="file"
                                                    accept="image/*"
                                                    className="hidden"
                                                    onChange={(event) => onFileInputChange(event, (file) =>
                                                        setFileOnItem(item.id, {
                                                            file,
                                                            fileNameField: "imageWrongAnswerFileName",
                                                            fileField: "imageWrongAnswerFile",
                                                            urlField: "imageWrongAnswerUrl",
                                                        })
                                                    )}
                                                />

                                                <label htmlFor={matchingAudioId} className="cursor-pointer text-[#626262] hover:text-black">
                                                    <FiVolume2 size={18} />
                                                </label>
                                                <input
                                                    id={matchingAudioId}
                                                    type="file"
                                                    accept="audio/*"
                                                    className="hidden"
                                                    onChange={(event) => onFileInputChange(event, (file) =>
                                                        setFileOnItem(item.id, {
                                                            file,
                                                            fileNameField: "soundWrongAnswerFileName",
                                                            fileField: "soundWrongAnswerFile",
                                                            urlField: "soundWrongAnswerUrl",
                                                        })
                                                    )}
                                                />
                                            </div>
                                        </div>
                                        {item.imageWrongAnswerFileName || item.soundWrongAnswerFileName ? (
                                            <div className="mt-1 flex flex-wrap gap-x-2 text-sm text-[#6b6b6b] absolute -bottom-4 left-0">
                                                {item.imageWrongAnswerFileName ? <span>รูป: {item.imageWrongAnswerFileName}</span> : null}
                                                {item.soundWrongAnswerFileName ? <span>เสียง: {item.soundWrongAnswerFileName}</span> : null}
                                            </div>
                                        ) : null}
                                    </div>

                                    <div className="flex items-center justify-center gap-3">
                                        <div className="flex items-center text-[#555]">
                                            <button
                                                type="button"
                                                onClick={() => moveItem(item.id, -1)}
                                                disabled={index === 0}
                                                className="cursor-pointer hover:text-black disabled:cursor-not-allowed disabled:opacity-30"
                                                title="เลื่อนขึ้น"
                                            >
                                                ↑
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => moveItem(item.id, 1)}
                                                disabled={index === items.length - 1}
                                                className="ml-2 cursor-pointer hover:text-black disabled:cursor-not-allowed disabled:opacity-30"
                                                title="เลื่อนลง"
                                            >
                                                ↓
                                            </button>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => removeItem(item.id)}
                                            disabled={!canRemoveItem}
                                            className="cursor-pointer text-[#ff8e8e] hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-50"
                                            title="ลบ"
                                        >
                                            <MdDeleteOutline size={22} />
                                        </button>
                                    </div>
                                </div>

                                {/* {item.hasHint ? (
                                    <div className="mt-2 ml-12 px-2 py-2">
                                        <div className="mb-1 flex items-center justify-between">
                                            <span className="text-lg font-medium text-[#5b5b5b]">คำใบ้</span>
                                        </div>
                                        <div className="relative">
                                            <div className="flex h-[38px] items-center rounded-md border border-[#a8a8a8] bg-white px-3 focus-within:border-blue-400">
                                                <input
                                                    value={item.hintText}
                                                    onChange={(event) => updateItem(item.id, (row) => ({ ...row, hasHint: true, hintText: event.target.value }))}
                                                    className="h-8 w-full min-w-0 text-lg outline-none"
                                                    placeholder="พิมพ์คำใบ้ (ไม่บังคับ)"
                                                />
                                                <div className="flex items-center gap-3 pl-2">
                                                    <label htmlFor={hintImageId} className="cursor-pointer text-[#62aee0]">
                                                        <LuImage size={18} />
                                                    </label>
                                                    <input
                                                        id={hintImageId}
                                                        type="file"
                                                        accept="image/*"
                                                        className="hidden"
                                                        onChange={(event) => onFileInputChange(event, (file) =>
                                                            setFileOnItem(item.id, {
                                                                file,
                                                                fileNameField: "hintImageFileName",
                                                                fileField: "hintImageFile",
                                                                urlField: "hintImageUrl",
                                                                forceEnableHint: true,
                                                            })
                                                        )}
                                                    />

                                                    <label htmlFor={hintAudioId} className="cursor-pointer text-[#4b4b4b]">
                                                        <FiVolume2 size={18} />
                                                    </label>
                                                    <input
                                                        id={hintAudioId}
                                                        type="file"
                                                        accept="audio/*"
                                                        className="hidden"
                                                        onChange={(event) => onFileInputChange(event, (file) =>
                                                            setFileOnItem(item.id, {
                                                                file,
                                                                fileNameField: "hintAudioFileName",
                                                                fileField: "hintAudioFile",
                                                                urlField: "hintAudioUrl",
                                                                forceEnableHint: true,
                                                            })
                                                        )}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => clearHint(item.id)}
                                                        className="cursor-pointer justify-self-center text-[#f18d8d] hover:text-red-500"
                                                    >
                                                        <MdDeleteOutline size={22} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        {item.hintImageFileName || item.hintAudioFileName ? (
                                            <div className="mt-1 flex flex-wrap gap-x-2 text-sm text-[#6b6b6b]">
                                                {item.hintImageFileName ? <span>รูปคำใบ้: {item.hintImageFileName}</span> : null}
                                                {item.hintAudioFileName ? <span>เสียงคำใบ้: {item.hintAudioFileName}</span> : null}
                                            </div>
                                        ) : null}
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => enableHint(item.id)}
                                        className="mt-2 text-start ml-14 cursor-pointer text-sm font-medium text-[#4d6ea9] hover:text-[#2f4d83]"
                                    >
                                        ต้องการเพิ่มคำใบ้หรือไม่?
                                    </button>
                                )} */}
                            </div>
                        );
                    })}
                </div>

                <div className="mt-5 ml-3">
                    <button
                        type="button"
                        onClick={addItem}
                        disabled={!canAddItem}
                        className="ml-10 rounded border border-[#c2c2c2] bg-[#e6e6e6] px-4 py-1.5 text-lg font-medium text-[#444] hover:bg-[#d6d6d6] disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
                    >
                        + เพิ่มรายการ
                    </button>
                </div>
            </div>

            <div className="flex flex-col gap-2 p-5 ml-10">
                <label className="text-sm font-bold text-[#555]">สมรรถนะ:</label>
                <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                    <label className="flex items-center gap-2 text-sm font-medium text-[#444] cursor-pointer">
                        <input
                            type="checkbox"
                            checked={gameSkills.skill_listening_speaking}
                            onChange={(event) => updateGameSkill("skill_listening_speaking", event.target.checked)}
                            className="h-4 w-4 rounded border-[#ccc]"
                        />
                        การฟัง การดู และการพูดเพื่อพัฒนาการคิด
                    </label>
                    <label className="flex items-center gap-2 text-sm font-medium text-[#444] cursor-pointer">
                        <input
                            type="checkbox"
                            checked={gameSkills.skill_reading}
                            onChange={(event) => updateGameSkill("skill_reading", event.target.checked)}
                            className="h-4 w-4 rounded border-[#ccc]"
                        />
                        การอ่านเพื่อพัฒนาการคิด
                    </label>
                    <label className="flex items-center gap-2 text-sm font-medium text-[#444] cursor-pointer">
                        <input
                            type="checkbox"
                            checked={gameSkills.skill_lang_nature}
                            onChange={(event) => updateGameSkill("skill_lang_nature", event.target.checked)}
                            className="h-4 w-4 rounded border-[#ccc]"
                        />
                        การเข้าใจธรรมชาติของภาษาไทยและการใช้ภาษาไทย
                    </label>
                    <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 text-sm font-medium text-[#444] cursor-pointer">
                            <input
                                type="checkbox"
                                checked={gameSkills.skill_writing}
                                onChange={(event) => updateGameSkill("skill_writing", event.target.checked)}
                                className="h-4 w-4 rounded border-[#ccc]"
                            />
                            การเขียนเพื่อพัฒนาการคิด
                        </label>
                    </div>
                </div>
            </div>

            <div className="mt-4 flex items-center justify-end gap-3">
                <button
                    type="button"
                    onClick={handleOpenPreview}
                    disabled={!canPreview}
                    className="cursor-pointer rounded bg-[#4E4E4E] px-7 py-2 text-lg font-semibold text-white shadow-[0_2px_0_rgba(0,0,0,0.2)] hover:bg-[#3d3d3d] disabled:cursor-not-allowed disabled:bg-[#9a9a9a] disabled:shadow-none"
                >
                    Preview
                </button>
                <button
                    type="button"
                    onClick={handleClickFinishButton}
                    disabled={isSubmitting}
                    className={`cursor-pointer rounded px-7 py-2 text-lg font-semibold text-white shadow-[0_2px_0_rgba(0,0,0,0.2)] disabled:cursor-not-allowed disabled:opacity-70 ${isUpdateMode ? "bg-[#1e4fa3] hover:bg-[#183f82]" : "bg-[#f4d735] text-[#4a4a4a] hover:bg-[#e0c91a]"}`}
                >
                    {isSubmitting ? "กำลังบันทึก..." : isUpdateMode ? "อัพเดท" : "บันทึก"}
                </button>
            </div>

            {isConfirmSaveOpen ? (
                <ConfirmPopUp
                    title={isUpdateMode ? "ยืนยันการอัพเดท" : "ยืนยันการบันทึก"}
                    message={isUpdateMode ? "ต้องการอัพเดทข้อมูลหรือไม่" : "ต้องการบันทึกข้อมูลหรือไม่"}
                    cancelText="ยกเลิก"
                    confirmText={isUpdateMode ? "อัพเดท" : "ยืนยัน"}
                    onCancel={handleCancelConfirmSave}
                    onConfirm={handleConfirmSave}
                    isConfirming={isSubmitting}
                />
            ) : null}

            {statusPopUp ? (
                <StatusPopUp
                    title={statusPopUp.title}
                    message={statusPopUp.message}
                    type={statusPopUp.type}
                    onClose={handleCloseStatusPopUp}
                />
            ) : null}

            {previewIframeUrl ? (
                <div className="fixed inset-0 z-70 flex items-center justify-center bg-black/50 p-4">
                    <div className="flex h-[90vh] w-full max-w-400 flex-col overflow-hidden rounded-lg bg-white shadow-[0_10px_30px_rgba(0,0,0,0.35)]">
                        <div className="flex items-center justify-between border-b border-[#e2e2e2] px-4 py-2">
                            <span className="text-base font-semibold text-[#2B2B2B]">Preview</span>
                            <button
                                type="button"
                                onClick={handleClosePreview}
                                className="cursor-pointer rounded bg-[#F54B4B] px-3 py-1 text-sm font-medium text-[#ffffff] hover:bg-[#F62A2A]"
                            >
                                ปิด
                            </button>
                        </div>
                        <iframe
                            src={previewIframeUrl}
                            title="Whack A Mole Preview"
                            className="h-full w-full border-0"
                            allowFullScreen
                        />
                    </div>
                </div>
            ) : null}
        </section>
    );
}
